import {FC, PropsWithChildren, ReactElement} from "react";
import {FiX} from "react-icons/fi";
import {IoChevronBackOutline} from "react-icons/io5";
import {BsFillQuestionCircleFill} from "react-icons/bs";
import classNames from "classnames";
import Tooltip from "@/components/Tooltip";
import "./ModalParts.scss";

/**
 * Themed Modal header / body / footer for the `widgets/Modal` family, replacing
 * the retired legacy modal header, body, and footer. Prop APIs mirror the
 * legacy originals so migration is a rename; styling is the anchor dialog look
 * (dp-modal-* classes), not the legacy palette.
 */

interface HeaderProps {
    title?: string | ReactElement;
    /** Close/back handler; when set, a dismiss control is rendered (see below). */
    toggle?: () => void;
    /** Optional "?" tooltip shown after the title. */
    tooltipContent?: string;
    isTitleBold?: boolean;
    /** Render a back chevron (instead of the X) on the left — for multi-step flows. */
    showLeftChevron?: boolean;
    /** Force the top-right X even when a left chevron is shown. */
    showXTopRight?: boolean;
    leftIcon?: FC<{ className?: string }>;
    centerTitle?: boolean;
}

export function ModalHeader(props: PropsWithChildren<HeaderProps>): ReactElement {
    const LeftIcon = props.leftIcon;
    const title =
        <h4
            className={classNames("dp-modal-header__title", {
                "dp-modal-header__title--bold": props.isTitleBold,
            })}
        >
            {props.title}
        </h4>;

    return (
        <div className="dp-modal-header">
            <div className="dp-modal-header__row">
                <div
                    className={classNames("dp-modal-header__lead", {
                        "dp-modal-header__lead--center": props.centerTitle,
                    })}
                >
                    {props.toggle && props.showLeftChevron &&
                        <button
                            type="button"
                            className="dp-modal-header__chevron"
                            onClick={props.toggle}
                            aria-label="Back"
                        >
                            <IoChevronBackOutline/>
                        </button>
                    }
                    {LeftIcon && <LeftIcon className="dp-modal-header__icon"/>}
                    {props.centerTitle ? <div className="dp-modal-header__center">{title}</div> : title}
                    {props.tooltipContent &&
                        <Tooltip outsideIcon={BsFillQuestionCircleFill} containerElementClassname="dp-modal-header">
                            <p>{props.tooltipContent}</p>
                        </Tooltip>
                    }
                </div>

                {(props.toggle && !props.showLeftChevron || props.toggle && props.showXTopRight) &&
                    <button
                        type="button"
                        className="dp-modal-header__close"
                        onClick={props.toggle}
                        aria-label="Close"
                    >
                        <FiX/>
                    </button>
                }
            </div>

            {props.children && <div className="dp-modal-header__children">{props.children}</div>}
        </div>
    );
}

interface BodyProps {
    className?: string;
    bodyRef?: (node: HTMLDivElement) => void;
}

export function ModalBody(props: PropsWithChildren<BodyProps>): ReactElement {
    return (
        <div ref={props.bodyRef} className={classNames("dp-modal-body", props.className)}>
            {props.children}
        </div>
    );
}

interface CloseProps {
    toggle: () => void;
}

/** Floating top-right close button for headerless modals (replaces the legacy modal close). */
export function ModalClose(props: CloseProps): ReactElement {
    return (
        <div className="dp-modal-close">
            <button
                type="button"
                className="dp-modal-close__x"
                onClick={props.toggle}
                aria-label="Close"
            >
                <FiX/>
            </button>
        </div>
    );
}

interface FooterProps {
    className?: string;
}

export function ModalFooter(props: PropsWithChildren<FooterProps>): ReactElement {
    return (
        <div className={classNames("dp-modal-footer", props.className)}>
            {props.children}
        </div>
    );
}
